import { useCallback, useEffect, useRef } from 'react';

/** 이 훅이 쌓은 방문 기록 칸 표시 */
const HISTORY_MARK: string = '__backToClose';
/** 되돌리기(popstate)가 이 시간 안에 안 오면 그냥 다음으로 */
const HISTORY_BACK_WAIT_MS: number = 500;

const hasMark = (id: string): boolean => (window.history.state as Record<string, unknown> | null)?.[HISTORY_MARK] === id;

/**
 * 칸을 쌓기 전 원래 칸의 스크롤 복원 방식. 스크롤 복원 방식은 방문 기록 칸마다 따로라, 원래 칸을 'manual' 로 두어야
 * 쌓은 칸을 되돌릴 때 브라우저가 스크롤을 연 순간 위치로 되감지 않는다. 원래 칸으로 돌아오면 되돌린다.
 */
const baseRestoration: Map<string, ScrollRestoration> = new Map();

const restoreBaseRestoration = (id: string): void => {
    const mode: ScrollRestoration | undefined = baseRestoration.get(id);
    if (mode === undefined || hasMark(id)) return;
    baseRestoration.delete(id);
    window.history.scrollRestoration = mode;
};

/** 쌓은 칸(id)을 되돌린 뒤 after 를 부른다 — 칸이 이미 없으면 바로 */
const goBackThen = (id: string, after?: () => void): void => {
    if (!hasMark(id)) {
        after?.();
        return;
    }
    let done: boolean = false;
    const finish = (): void => {
        if (done) return;
        done = true;
        window.removeEventListener('popstate', finish);
        window.clearTimeout(timer);
        if (hasMark(id)) {
            // popstate 가 안 왔다 — 두 번 되돌리지 않게 표시만 지운다
            const state: Record<string, unknown> = { ...((window.history.state as Record<string, unknown> | null) ?? {}) };
            delete state[HISTORY_MARK];
            window.history.replaceState(state, '');
        }
        restoreBaseRestoration(id);
        after?.();
    };
    window.addEventListener('popstate', finish);
    const timer: number = window.setTimeout(finish, HISTORY_BACK_WAIT_MS);
    window.history.back();
};

/**
 * 펼침·메뉴를 연 채 뒤로 가기(안드로이드 뒤로 키·브라우저 뒤로·iOS 가장자리 밀기)를 누르면 이전 페이지로 나가지 않고 펼침만 닫는다.
 * 공용 시트(FilterPanel)와 같은 방식 — 열 때 방문 기록 한 칸(같은 주소, 표시만 단 칸)을 쌓고, 뒤로 가기로 그 칸이 빠지면 닫는다.
 *
 * - 다른 방법(바깥 누르기·항목 고르기·Esc)으로 닫으면 쌓은 칸을 되돌린다. 되돌릴 때 브라우저가 스크롤을 연 순간 위치로 되감지 않게
 *   쌓는 동안 원래 칸을 `scrollRestoration = 'manual'` 로 두고 돌아오면 되돌린다(정렬 펼침은 화면을 굴리면 닫힌다 — 굴린 자리를 지킨다).
 * - 닫으면서 이동·주소 바꾸기를 할 때(메뉴 항목·정렬 고르기)는 반드시 `leave(after)` 로 부른다 — 쌓은 칸을 먼저 되돌린 뒤 after 를 부르므로
 *   되돌리기가 새로 쌓은 이동을 지우지 않는다(되돌리기 직후 바로 pushState 하면 브라우저가 이동한 칸에서 한 칸 뒤로 가 버린다).
 *
 * @param open 열려 있는지
 * @param onClose 뒤로 가기로 닫을 때 부른다(펼침 상태를 닫기만 — 다시 history 를 건드리지 말 것)
 * @returns leave(after) — 쌓은 칸을 되돌린 뒤 after 를 부른다(칸이 없으면 바로)
 */
export const useBackToClose = (open: boolean, onClose: () => void): ((after?: () => void) => void) => {
    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;
    /** 지금 쌓아 둔 칸 */
    const idRef = useRef<string | null>(null);
    /** leave 로 되돌리는 중 — 닫히며 도는 정리에서 또 되돌리지 않게 */
    const leavingRef = useRef<boolean>(false);

    useEffect(() => {
        if (!open) return undefined;
        const id: string = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
        idRef.current = id;
        const base: Record<string, unknown> = (window.history.state as Record<string, unknown> | null) ?? {};
        baseRestoration.set(id, window.history.scrollRestoration);
        window.history.scrollRestoration = 'manual';
        window.history.pushState({ ...base, [HISTORY_MARK]: id }, '');
        const onPopState = (): void => {
            if (hasMark(id)) return;
            restoreBaseRestoration(id);
            if (!leavingRef.current) onCloseRef.current();
        };
        window.addEventListener('popstate', onPopState);
        return () => {
            window.removeEventListener('popstate', onPopState);
            if (idRef.current === id) idRef.current = null;
            // 뒤로 가기·leave 가 아닌 길(바깥 누르기·Esc·화면 굴림)로 닫혔으면 쌓은 칸을 치운다
            if (!leavingRef.current && hasMark(id)) goBackThen(id);
        };
    }, [open]);

    return useCallback((after?: () => void): void => {
        const id: string | null = idRef.current;
        if (!id || leavingRef.current) {
            after?.();
            return;
        }
        leavingRef.current = true;
        goBackThen(id, () => {
            leavingRef.current = false;
            after?.();
        });
    }, []);
};
